import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  LifeBuoy,
  Plus,
  Edit2,
  Trash2,
  Eye,
  X,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  Filter,
  MessageSquare,
  Cpu,
  User,
  Calendar,
  Upload,
} from "react-feather";
import Select, { components } from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  listOfSupportTicketsApi,
  createSupportTicketApi,
  editSupportTicketApi,
  deleteSupportTicketApi,
  updateSupportTicketStatusApi,
} from "../../Api/supportApi";
import { fileUploadApi } from "../../Api/common";
import { BaseSupportURL } from "../../Api/Service";
import {
  checkStatusCodeSuccess,
  getLocalItem,
  errHandler,
  extractDateAsM,
  isRequired,
} from "../../Constant/common";
import { supportCenterTitle } from "../../Constant/title";
import {
  supportLabel,
  SUPPORT_TYPES,
  EMPLOYEE_CATEGORIES,
  PRODUCT_CATEGORIES,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  getStatusBadgeStyle,
  getPriorityBadgeStyle,
  getSupportTypeBadgeStyle,
  SUPPORT_ACCESSOR_KEYS,
} from "../../Constant/Support";
import BaseModal from "../../BaseComponents/BaseModal";
import { useSelectedOrganization } from "../OrganizationContext";

// ── Helpers ────────────────────────────────────────────────────────────────

const CustomInput = (props) => <components.Input {...props} data-rs-input="true" />;

const toSelectOption = (arr) => arr.map((o) => ({ value: o.value, label: o.label }));

const findLabel = (arr, value) => arr.find((o) => o.value === value)?.label ?? value ?? "—";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return extractDateAsM(d);
  } catch {
    return "—";
  }
};

const parseAttachments = (attachments) => {
  if (!attachments) return [];
  if (Array.isArray(attachments)) return attachments;
  if (typeof attachments === "string") {
    const trimmed = attachments.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // ignore & fallback
      }
    }
    return trimmed
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const getAttachmentUrl = (imgName) => {
  if (!imgName || !BaseSupportURL) return "";
  const baseUrl = BaseSupportURL.endsWith("/") ? BaseSupportURL : BaseSupportURL + "/";
  const cleanName = imgName.startsWith("/") ? imgName.slice(1) : imgName;
  return baseUrl + cleanName;
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
};

// ── Badge Components ────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const { bg, color } = getStatusBadgeStyle(status);
  const icons = {
    open: AlertCircle,
    in_progress: Clock,
    resolved: CheckCircle,
    closed: XCircle,
  };
  const Icon = icons[status?.toLowerCase()] ?? AlertCircle;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: bg,
        color,
        border: `1px solid ${color}40`,
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={10} strokeWidth={2.5} />
      {findLabel(STATUS_OPTIONS, status)}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const { bg, color } = getPriorityBadgeStyle(priority);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: bg,
        color,
        border: `1px solid ${color}40`,
        whiteSpace: "nowrap",
      }}
    >
      {findLabel(PRIORITY_OPTIONS, priority)}
    </span>
  );
};

const SupportTypeBadge = ({ type }) => {
  const { bg, color } = getSupportTypeBadgeStyle(type);
  const Icon = type === "employee" ? MessageSquare : Cpu;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: bg,
        color,
        border: `1px solid ${color}40`,
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={10} strokeWidth={2.5} />
      {type === "employee" ? "Employee" : "Product"}
    </span>
  );
};

// ── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({ label, count, color, bg, border, icon: Icon }) => (
  <div
    className="animate-slide-up"
    style={{
      flex: "1 1 120px",
      minWidth: 110,
      padding: "14px 16px",
      borderRadius: 14,
      background: bg,
      border: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      gap: 12,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: `${color}20`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={16} strokeWidth={2.5} style={{ color }} />
    </div>
    <div>
      <p
        style={{
          fontSize: "0.65rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--text-muted)",
          margin: 0,
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: "1.3rem", fontWeight: 800, color, margin: "2px 0 0" }}>{count}</p>
    </div>
  </div>
);

// ── Section Tab ─────────────────────────────────────────────────────────────

const SectionTab = ({ active, icon: Icon, label, description, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flex: "1 1 200px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "16px 20px",
      borderRadius: 14,
      border: active ? `2px solid ${color}` : "1px solid var(--glass-border)",
      background: active ? `${color}10` : "var(--glass-bg)",
      cursor: "pointer",
      transition: "all 0.2s",
      textAlign: "left",
    }}
  >
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: 12,
        background: active ? `${color}20` : "var(--icon-btn-bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={18} strokeWidth={2} style={{ color: active ? color : "var(--text-muted)" }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <p
        style={{
          fontSize: "0.9rem",
          fontWeight: 700,
          color: active ? color : "var(--text-primary)",
          margin: 0,
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: "0.7rem",
          color: "var(--text-muted)",
          margin: "2px 0 0",
          lineHeight: 1.4,
        }}
      >
        {description}
      </p>
    </div>
    {active && (
      <div
        style={{
          marginLeft: "auto",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
          boxShadow: `0 0 0 3px ${color}30`,
        }}
      />
    )}
  </button>
);

// ── Ticket Card (mobile/compact) ────────────────────────────────────────────

const TicketCard = ({ ticket, onView, onEdit, onDelete, isAdmin }) => (
  <div
    className="glass-sm"
    style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}
  >
    <div
      style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}
    >
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            fontSize: "0.75rem",
            color: "var(--text-muted)",
            fontWeight: 600,
            margin: 0,
          }}
        >
          #{ticket?.ticket_no ?? "—"}
        </p>
        <p
          style={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "var(--text-primary)",
            margin: "2px 0 0",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ticket?.subject ?? "—"}
        </p>
      </div>
      <StatusBadge status={ticket?.status} />
    </div>

    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      <SupportTypeBadge type={ticket?.support_type} />
      <PriorityBadge priority={ticket?.priority} />
      <span
        style={{
          fontSize: "0.7rem",
          padding: "3px 9px",
          borderRadius: 999,
          background: "var(--icon-btn-bg)",
          color: "var(--text-muted)",
          border: "1px solid var(--glass-border)",
        }}
      >
        {findLabel(
          ticket?.support_type === "employee" ? EMPLOYEE_CATEGORIES : PRODUCT_CATEGORIES,
          ticket?.category,
        )}
      </span>
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
        {fmtDate(ticket?.createdAt)}
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          className="btn btn-icon-action btn-view"
          title={supportLabel.VIEW_TICKET}
          onClick={() => onView(ticket)}
        >
          <Eye size={13} strokeWidth={2} />
        </button>
        {isAdmin && (
          <>
            <button
              type="button"
              className="btn btn-icon-action btn-edit"
              title={supportLabel.EDIT_TICKET}
              onClick={() => onEdit(ticket)}
              disabled={ticket?.status?.toLowerCase() === "closed"}
              style={{
                opacity: ticket?.status?.toLowerCase() === "closed" ? 0.5 : 1,
                cursor: ticket?.status?.toLowerCase() === "closed" ? "not-allowed" : "pointer",
              }}
            >
              <Edit2 size={13} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="btn btn-icon-action btn-delete"
              title={supportLabel.DELETE_TICKET}
              onClick={() => onDelete(ticket)}
            >
              <Trash2 size={13} strokeWidth={2} />
            </button>
          </>
        )}
      </div>
    </div>
  </div>
);

// ── Ticket Form ─────────────────────────────────────────────────────────────

const TicketForm = ({
  formik,
  isEdit,
  isViewOnly,
  canEmployeeSupport = false,
  canProductSupport = false,
}) => {
  const [uploading, setUploading] = useState(false);

  const supportTypeOptions = toSelectOption(
    SUPPORT_TYPES.filter((t) => {
      if (t.value === formik.values.support_type) return true;
      if (t.value === "employee") return canEmployeeSupport;
      if (t.value === "product") return canProductSupport;
      return false;
    }),
  );
  const selectedType = formik.values.support_type;
  const categoryOptions = toSelectOption(
    selectedType === "product" ? PRODUCT_CATEGORIES : EMPLOYEE_CATEGORIES,
  );
  const priorityOptions = toSelectOption(PRIORITY_OPTIONS);
  const statusOptions = toSelectOption(STATUS_OPTIONS);

  const currentImages = formik.values.attachment
    ? formik.values.attachment.split(",").filter(Boolean)
    : [];

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (currentImages.length + files.length > 3) {
      toast.error("You can only upload up to 3 images.");
      e.target.value = "";
      return;
    }

    setUploading(true);

    for (const file of files) {
      if (file.size > 1_048_576) {
        toast.error(`"${file.name}" exceeds the 1MB size limit.`);
        continue;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
        toast.error(`"${file.name}" is not a supported image format.`);
        continue;
      }

      const formData = new FormData();
      formData.append("files", file);
      formData.append("folderName", "support");

      try {
        const resp = await fileUploadApi(formData);
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const newFilename = resp?.data[0];
          if (newFilename) {
            currentImages.push(newFilename);
          }
        } else {
          toast.error(resp?.message || "Failed to upload file.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Upload error.");
      }
    }

    formik.setFieldValue("attachment", currentImages.join(","));
    setUploading(false);
    e.target.value = "";
  };

  const handleRemoveImage = (indexToRemove) => {
    const updated = currentImages.filter((_, idx) => idx !== indexToRemove);
    formik.setFieldValue("attachment", updated.join(","));
  };

  const fieldStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Support Type */}
      <div style={fieldStyle}>
        <label className="form-label">
          {supportLabel.SUPPORT_TYPE_LABEL} {!isViewOnly && isRequired}
        </label>
        <Select
          classNamePrefix="react-select"
          components={{ Input: CustomInput }}
          options={supportTypeOptions}
          isDisabled={isViewOnly}
          value={supportTypeOptions.find((o) => o.value === formik.values.support_type) ?? null}
          onChange={(opt) => {
            formik.setFieldValue("support_type", opt?.value ?? "");
            formik.setFieldValue("category", "");
          }}
          placeholder="Select support type..."
        />
        {formik.touched.support_type && formik.errors.support_type && (
          <div className="error text-danger" style={{ fontSize: "0.75rem" }}>
            {formik.errors.support_type}
          </div>
        )}
      </div>

      {/* Category */}
      <div style={fieldStyle}>
        <label className="form-label">
          {supportLabel.CATEGORY_LABEL} {!isViewOnly && isRequired}
        </label>
        <Select
          classNamePrefix="react-select"
          components={{ Input: CustomInput }}
          options={categoryOptions}
          isDisabled={isViewOnly || !selectedType}
          value={categoryOptions.find((o) => o.value === formik.values.category) ?? null}
          onChange={(opt) => formik.setFieldValue("category", opt?.value ?? "")}
          placeholder={selectedType ? "Select category..." : "Select support type first..."}
        />
        {formik.touched.category && formik.errors.category && (
          <div className="error text-danger" style={{ fontSize: "0.75rem" }}>
            {formik.errors.category}
          </div>
        )}
      </div>

      {/* Subject */}
      <div style={fieldStyle}>
        <label className="form-label">
          {supportLabel.SUBJECT_LABEL} {!isViewOnly && isRequired}
        </label>
        <input
          type="text"
          className="form-control"
          placeholder="Brief summary of your issue..."
          disabled={isViewOnly}
          {...formik.getFieldProps("subject")}
        />
        {formik.touched.subject && formik.errors.subject && (
          <div className="error text-danger" style={{ fontSize: "0.75rem" }}>
            {formik.errors.subject}
          </div>
        )}
      </div>

      {/* Description */}
      <div style={fieldStyle}>
        <label className="form-label">
          {supportLabel.DESCRIPTION_LABEL} {!isViewOnly && isRequired}
        </label>
        <textarea
          className="form-control"
          rows={4}
          placeholder="Describe your issue in detail..."
          disabled={isViewOnly}
          style={{ resize: "vertical", height: "auto", minHeight: 100 }}
          {...formik.getFieldProps("description")}
        />
        {formik.touched.description && formik.errors.description && (
          <div className="error text-danger" style={{ fontSize: "0.75rem" }}>
            {formik.errors.description}
          </div>
        )}
      </div>

      {/* Priority */}
      <div style={fieldStyle}>
        <label className="form-label">
          {supportLabel.PRIORITY_LABEL} {!isViewOnly && isRequired}
        </label>
        <Select
          classNamePrefix="react-select"
          components={{ Input: CustomInput }}
          options={priorityOptions}
          isDisabled={isViewOnly}
          value={priorityOptions.find((o) => o.value === formik.values.priority) ?? null}
          onChange={(opt) => formik.setFieldValue("priority", opt?.value ?? "")}
          placeholder="Select priority..."
        />
        {formik.touched.priority && formik.errors.priority && (
          <div className="error text-danger" style={{ fontSize: "0.75rem" }}>
            {formik.errors.priority}
          </div>
        )}
      </div>

      {/* Image Upload Option */}
      <div style={fieldStyle}>
        <label className="form-label">{supportLabel.ATTACHMENT_LABEL}</label>

        {/* Thumbnails grid */}
        {currentImages.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
            {currentImages.map((imgName, idx) => {
              const imgUrl = getAttachmentUrl(imgName);
              return (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    width: 72,
                    height: 72,
                    borderRadius: 10,
                    border: "1px solid var(--glass-border)",
                    overflow: "hidden",
                    background: "var(--icon-btn-bg)",
                  }}
                >
                  <img
                    src={imgUrl}
                    alt={`Attachment ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {!isViewOnly && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: "rgba(244,63,94,0.85)",
                        color: "#fff",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      title="Remove image"
                    >
                      <X size={10} strokeWidth={3} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Dropzone/Upload Button */}
        {!isViewOnly && currentImages.length < 3 && (
          <div
            style={{
              position: "relative",
              border: "1.5px dashed var(--glass-border)",
              borderRadius: 10,
              padding: "16px",
              textAlign: "center",
              background: "var(--icon-btn-bg)",
              cursor: uploading ? "not-allowed" : "pointer",
              transition: "border-color 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!uploading) e.currentTarget.style.borderColor = "var(--brand-indigo)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--glass-border)";
            }}
          >
            <input
              type="file"
              multiple
              accept="image/*"
              disabled={uploading}
              onChange={handleFileChange}
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                cursor: uploading ? "not-allowed" : "pointer",
              }}
            />
            {uploading ? (
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "2px solid rgba(99,102,241,0.3)",
                    borderTopColor: "#6366f1",
                    animation: "spin 0.7s linear infinite",
                  }}
                />
                <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontWeight: 600 }}>
                  Uploading...
                </span>
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
              >
                <Upload size={18} strokeWidth={2} style={{ color: "var(--text-muted)" }} />
                <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: 0 }}>
                  Upload images (max 3 · under 1MB each)
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status (edit only) */}
      {isEdit && (
        <div style={fieldStyle}>
          <label className="form-label">{supportLabel.STATUS_LABEL}</label>
          <Select
            classNamePrefix="react-select"
            components={{ Input: CustomInput }}
            options={statusOptions}
            isDisabled={isViewOnly}
            value={statusOptions.find((o) => o.value === formik.values.status) ?? null}
            onChange={(opt) => formik.setFieldValue("status", opt?.value ?? "")}
            placeholder="Select status..."
          />
        </div>
      )}
    </div>
  );
};

// ── Ticket Detail View ──────────────────────────────────────────────────────

const TicketDetail = ({ ticket, onClose, onEdit, onDelete, isAdmin, onStatusUpdate }) => {
  const width = useWindowWidth();
  const [statusLoader, setStatusLoader] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  if (!ticket) return null;

  const categories = ticket?.support_type === "product" ? PRODUCT_CATEGORIES : EMPLOYEE_CATEGORIES;
  const ticketImages = parseAttachments(ticket?.attachments ?? ticket?.attachment);

  const handleQuickStatus = (newStatus) => {
    if (!ticket?.id) return;
    setStatusLoader(true);
    setShowStatusMenu(false);
    updateSupportTicketStatusApi(ticket.id, { status: newStatus })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success("Status updated.");
          onStatusUpdate(ticket.id, newStatus);
        } else {
          toast.error(resp?.message || "Failed to update status.");
        }
      })
      .catch(errHandler)
      .finally(() => setStatusLoader(false));
  };

  return (
    <div
      className="glass animate-scale-in"
      style={{
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        borderTop: "3px solid var(--brand-indigo)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          borderBottom: "1px solid var(--glass-border)",
          paddingBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, margin: 0 }}
          >
            Ticket #{ticket?.ticket_no ?? "—"}
          </p>
          <h3
            style={{
              fontSize: "1rem",
              fontWeight: 800,
              color: "var(--text-primary)",
              margin: "4px 0 0",
              lineHeight: 1.3,
            }}
          >
            {ticket?.subject}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "1px solid var(--glass-border)",
            background: "var(--icon-btn-bg)",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(244,63,94,0.1)";
            e.currentTarget.style.color = "#f43f5e";
            e.currentTarget.style.borderColor = "rgba(244,63,94,0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--icon-btn-bg)";
            e.currentTarget.style.color = "var(--text-muted)";
            e.currentTarget.style.borderColor = "var(--glass-border)";
          }}
        >
          <X size={14} strokeWidth={2.5} />
        </button>
      </div>

      {/* Badges row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <StatusBadge status={ticket?.status} />
        <PriorityBadge priority={ticket?.priority} />
        <SupportTypeBadge type={ticket?.support_type} />
        <span
          style={{
            fontSize: "0.72rem",
            padding: "3px 10px",
            borderRadius: 999,
            background: "var(--icon-btn-bg)",
            color: "var(--text-muted)",
            border: "1px solid var(--glass-border)",
          }}
        >
          {findLabel(categories, ticket?.category)}
        </span>
      </div>

      {/* Description */}
      <div>
        <p
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            margin: "0 0 6px",
          }}
        >
          Description
        </p>
        <div
          style={{
            background: "rgba(99,102,241,0.02)",
            border: "1px solid var(--glass-border)",
            borderLeft: "3px solid var(--brand-indigo)",
            borderRadius: "0 10px 10px 0",
            padding: "12px 14px",
            fontSize: "0.8125rem",
            color: "var(--text-secondary)",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
          }}
        >
          {ticket?.description || "No description provided."}
        </div>
      </div>

      {/* Attachments */}
      {ticketImages.length > 0 && (
        <div>
          <p
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              margin: "0 0 6px",
            }}
          >
            Attachments ({ticketImages.length})
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {ticketImages.map((imgName, idx) => {
              const imgUrl = getAttachmentUrl(imgName);
              return (
                <a
                  key={idx}
                  href={imgUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block",
                    width: 68,
                    height: 68,
                    borderRadius: 10,
                    border: "1px solid var(--glass-border)",
                    overflow: "hidden",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  title="Click to view full image"
                >
                  <img
                    src={imgUrl}
                    alt={`Attachment ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Meta */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: width < 480 || (width >= 992 && width < 1200) ? "1fr" : "1fr 1fr",
          gap: 12,
        }}
      >
        {[
          { label: "Raised By", value: ticket?.raised_by ?? ticket?.raisedBy ?? "—", icon: User },
          {
            label: "Assigned To",
            value: ticket?.assigned_to ?? ticket?.assignedTo ?? "—",
            icon: User,
          },
          { label: "Created", value: fmtDate(ticket?.createdAt), icon: Calendar },
          { label: "Last Updated", value: fmtDate(ticket?.updatedAt), icon: Clock },
        ].map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "rgba(99,102,241,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={13} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  margin: 0,
                }}
              >
                {label}
              </p>
              <p
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  margin: "2px 0 0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={value}
              >
                {value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      {isAdmin && (
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            borderTop: "1px solid var(--glass-border)",
            paddingTop: 14,
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            style={{
              fontSize: "0.78rem",
              padding: "7px 14px",
              color: "#fff",
              opacity: ticket?.status?.toLowerCase() === "closed" ? 0.5 : 1,
              cursor: ticket?.status?.toLowerCase() === "closed" ? "not-allowed" : "pointer",
            }}
            onClick={() => onEdit(ticket)}
            disabled={ticket?.status?.toLowerCase() === "closed"}
          >
            <Edit2 size={13} strokeWidth={2} style={{ color: "#fff" }} />
            <span style={{ color: "#fff", marginLeft: 4 }}>Edit</span>
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ fontSize: "0.78rem", padding: "7px 14px" }}
            onClick={() => onDelete(ticket)}
          >
            <Trash2 size={13} strokeWidth={2} />
            Delete
          </button>

          {/* Quick status change */}
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: "0.78rem", padding: "7px 14px" }}
              disabled={statusLoader}
              onClick={() => setShowStatusMenu((p) => !p)}
            >
              <ChevronDown size={13} strokeWidth={2.5} />
              Change Status
            </button>
            {showStatusMenu && (
              <div
                style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  right: 0,
                  zIndex: 999,
                  background: "var(--dropdown-bg)",
                  border: "1px solid var(--dropdown-border)",
                  borderRadius: 10,
                  padding: "6px",
                  minWidth: 140,
                  boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
                }}
              >
                {STATUS_OPTIONS.filter((o) => o.value !== ticket?.status).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleQuickStatus(opt.value)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "7px 12px",
                      borderRadius: 7,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: "0.78rem",
                      color: "var(--text-primary)",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(99,102,241,0.1)")
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Validation schema ───────────────────────────────────────────────────────

const ticketSchema = Yup.object({
  support_type: Yup.string().required("Support type is required"),
  category: Yup.string().required("Category is required"),
  subject: Yup.string()
    .trim()
    .min(5, "Subject must be at least 5 characters")
    .required("Subject is required"),
  description: Yup.string()
    .trim()
    .min(10, "Description must be at least 10 characters")
    .required("Description is required"),
  priority: Yup.string().required("Priority is required"),
});

// ── Skeleton loader ──────────────────────────────────────────────────────

const SkeletonRow = () => (
  <div
    style={{
      padding: "14px 0",
      borderBottom: "1px solid var(--glass-border)",
      display: "flex",
      gap: 16,
      alignItems: "center",
    }}
  >
    {[100, 140, 90, 80, 70, 70].map((w, i) => (
      <div
        key={i}
        style={{
          width: w,
          height: 16,
          borderRadius: 6,
          background: "var(--icon-btn-bg)",
          animation: "shimmer 1.5s infinite",
        }}
      />
    ))}
  </div>
);

// ── Main Component ──────────────────────────────────────────────────────────

const SupportCenter = () => {
  const width = useWindowWidth();
  const { selectedOrganization } = useSelectedOrganization();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const role = getLocalItem("role") ?? "";
  const activePermissions = useMemo(() => {
    try {
      return JSON.parse(getLocalItem("permissions")) || [];
    } catch {
      return [];
    }
  }, []);

  const hasPermission = useCallback(
    (name) => {
      return activePermissions.some(
        (p) =>
          p?.module_name?.trim()?.toLowerCase() === name.toLowerCase() ||
          p?.parent_module_name?.trim()?.toLowerCase() === name.toLowerCase(),
      );
    },
    [activePermissions],
  );

  const hasSpecificSupportPermissions = useMemo(() => {
    return activePermissions.some(
      (p) =>
        p?.module_name === "Employee Support" ||
        p?.module_name === "Product Support" ||
        p?.module_name === "Manage Support",
    );
  }, [activePermissions]);

  const canEmployeeSupport = useMemo(() => {
    if (role === "SuperAdmin") return true;
    if (hasPermission("Employee Support")) return true;
    if (!hasSpecificSupportPermissions && hasPermission("Support")) return true;
    return false;
  }, [role, hasPermission, hasSpecificSupportPermissions]);

  const canProductSupport = useMemo(() => {
    if (role === "SuperAdmin") return true;
    if (hasPermission("Product Support")) return true;
    if (!hasSpecificSupportPermissions && hasPermission("Support")) return true;
    return false;
  }, [role, hasPermission, hasSpecificSupportPermissions]);

  const canManageSupport = useMemo(() => {
    if (role === "SuperAdmin") return true;
    if (hasPermission("Manage Support")) return true;
    if (!hasSpecificSupportPermissions && hasPermission("Support")) return true;
    return false;
  }, [role, hasPermission, hasSpecificSupportPermissions]);

  const [activeSection, setActiveSection] = useState(() => {
    if (canEmployeeSupport) return "employee";
    if (canProductSupport) return "product";
    return "employee";
  }); // "employee" | "product"

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const showCompact = !!selectedTicket;

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);

  useEffect(() => {
    document.title = `${supportCenterTitle} — HR Fusions`;
  }, []);

  // ── Fetch tickets ─────────────────────────────────────────────────────────

  const fetchTickets = useCallback(() => {
    setIsLoading(true);
    const orgId = selectedOrganization ?? getLocalItem("organizationId");
    const payload = {
      pageNumber: 1,
      pageSize: 500,
    };
    if (role === "SuperAdmin" && orgId && orgId !== "null" && orgId !== "undefined") {
      payload.organization_id = Number(orgId);
    }
    listOfSupportTicketsApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const list = resp?.data?.listOfTickets ?? [];
          setTickets(Array.isArray(list) ? list : []);
        } else {
          setTickets([]);
        }
      })
      .catch(() => setTickets([]))
      .finally(() => setIsLoading(false));
  }, [selectedOrganization, role]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchTickets();
  }, [fetchTickets]);

  // ── Formik ────────────────────────────────────────────────────────────────

  const formik = useFormik({
    initialValues: {
      support_type:
        isEdit && selectedTicket ? (selectedTicket.support_type ?? activeSection) : activeSection,
      category: isEdit && selectedTicket ? (selectedTicket.category ?? "") : "",
      subject: isEdit && selectedTicket ? (selectedTicket.subject ?? "") : "",
      description: isEdit && selectedTicket ? (selectedTicket.description ?? "") : "",
      priority: isEdit && selectedTicket ? (selectedTicket.priority ?? "medium") : "medium",
      status: isEdit && selectedTicket ? (selectedTicket.status ?? "open") : "open",
      attachment:
        isEdit && selectedTicket
          ? parseAttachments(selectedTicket.attachments ?? selectedTicket.attachment).join(",")
          : "",
    },
    validationSchema: ticketSchema,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      setSubmitLoader(true);
      const orgId = selectedOrganization ?? getLocalItem("organizationId");
      const payload = { ...values };
      payload.attachments = parseAttachments(values.attachment);
      delete payload.attachment;
      if (orgId && orgId !== "null" && orgId !== "undefined") {
        payload.organization_id = Number(orgId);
      }

      const call = isEdit
        ? editSupportTicketApi(selectedTicket?.id, payload)
        : createSupportTicketApi(payload);

      call
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(isEdit ? "Ticket updated." : "Ticket raised successfully.");
            setShowFormModal(false);
            const wasEdit = isEdit;
            setIsEdit(false);
            resetForm();
            fetchTickets();
            if (wasEdit && selectedTicket) {
              setSelectedTicket((prev) => ({
                ...prev,
                ...values,
                attachments: parseAttachments(values.attachment),
              }));
            }
          } else {
            toast.error(resp?.message || "Something went wrong.");
          }
        })
        .catch(errHandler)
        .finally(() => setSubmitLoader(false));
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleNewTicket = () => {
    setIsEdit(false);
    setIsViewOnly(false);
    setSelectedTicket(null);
    formik.resetForm({
      values: {
        support_type: activeSection,
        category: "",
        subject: "",
        description: "",
        priority: "medium",
        status: "open",
        attachment: "",
      },
    });
    setShowFormModal(true);
  };

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setIsViewOnly(true);
    setIsEdit(false);
    const attachmentsString = parseAttachments(ticket?.attachments ?? ticket?.attachment).join(",");
    formik.resetForm({ values: { ...ticket, attachment: attachmentsString } });
    setShowFormModal(false);
  };

  const handleEdit = (ticket) => {
    if (ticket?.status?.toLowerCase() === "closed") {
      toast.warning("Closed tickets cannot be edited.");
      return;
    }
    setSelectedTicket(ticket);
    setIsEdit(true);
    setIsViewOnly(false);
    const attachmentsString = parseAttachments(ticket?.attachments ?? ticket?.attachment).join(",");
    formik.resetForm({
      values: {
        support_type: ticket?.support_type ?? "employee",
        category: ticket?.category ?? "",
        subject: ticket?.subject ?? "",
        description: ticket?.description ?? "",
        priority: ticket?.priority ?? "medium",
        status: ticket?.status ?? "open",
        attachment: attachmentsString,
      },
    });
    setShowFormModal(true);
  };

  const handleDeleteClick = (ticket) => {
    setDeleteTarget(ticket);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget?.id) return;
    setDeleteLoader(true);
    deleteSupportTicketApi(deleteTarget.id)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success("Ticket deleted.");
          setShowDeleteModal(false);
          setDeleteTarget(null);
          if (selectedTicket?.id === deleteTarget.id) setSelectedTicket(null);
          fetchTickets();
        } else {
          toast.error(resp?.message || "Failed to delete.");
        }
      })
      .catch(errHandler)
      .finally(() => setDeleteLoader(false));
  };

  const handleStatusUpdate = (ticketId, newStatus) => {
    setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t)));
    if (selectedTicket?.id === ticketId) {
      setSelectedTicket((prev) => ({ ...prev, status: newStatus }));
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSelectedTicket(null);
    setStatusFilter("all");
    setPriorityFilter("all");
    setSearchQuery("");
  };

  // ── Filtered & computed data ───────────────────────────────────────────────

  const sectionTickets = useMemo(
    () => tickets.filter((t) => t?.support_type === activeSection),
    [tickets, activeSection],
  );

  const filteredTickets = useMemo(() => {
    return sectionTickets.filter((t) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (t?.subject ?? "").toLowerCase().includes(q) ||
        (t?.ticket_no ?? "").toLowerCase().includes(q) ||
        (t?.description ?? "").toLowerCase().includes(q);
      const matchesStatus = statusFilter === "all" || t?.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || t?.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [sectionTickets, searchQuery, statusFilter, priorityFilter]);

  const useTableView = useMemo(() => {
    if (selectedTicket) {
      return width >= 1200;
    }
    return width >= 992;
  }, [width, selectedTicket]);

  // Stats
  const statsData = useMemo(() => {
    const all = sectionTickets;
    return {
      total: all.length,
      open: all.filter((t) => t?.status === "open").length,
      inProgress: all.filter((t) => t?.status === "in_progress").length,
      resolved: all.filter((t) => t?.status === "resolved").length,
      closed: all.filter((t) => t?.status === "closed").length,
    };
  }, [sectionTickets]);

  const employeeCount = tickets.filter((t) => t?.support_type === "employee").length;
  const productCount = tickets.filter((t) => t?.support_type === "product").length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Page header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
            }}
          >
            <LifeBuoy size={20} strokeWidth={2} style={{ color: "#fff" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.3rem",
                fontWeight: 800,
                color: "var(--text-primary)",
                margin: 0,
              }}
            >
              {supportLabel.PAGE_TITLE}
            </h1>
            <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "2px 0 0" }}>
              {supportLabel.PAGE_SUBTITLE_ALL}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNewTicket}
          style={{ gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} />
          {supportLabel.NEW_TICKET}
        </button>
      </div>

      {/* Section tabs */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {canEmployeeSupport && (
          <SectionTab
            active={activeSection === "employee"}
            icon={MessageSquare}
            label={supportLabel.EMPLOYEE_SUPPORT}
            description={`${employeeCount} ticket${employeeCount !== 1 ? "s" : ""} · ${supportLabel.EMPLOYEE_SUPPORT_DESC}`}
            color="#6366f1"
            onClick={() => handleSectionChange("employee")}
          />
        )}
        {canProductSupport && (
          <SectionTab
            active={activeSection === "product"}
            icon={Cpu}
            label={supportLabel.PRODUCT_SUPPORT}
            description={`${productCount} ticket${productCount !== 1 ? "s" : ""} · ${supportLabel.PRODUCT_SUPPORT_DESC}`}
            color="#8b5cf6"
            onClick={() => handleSectionChange("product")}
          />
        )}
      </div>

      {/* Info banner */}
      <div
        style={{
          padding: "12px 16px",
          borderRadius: 12,
          background:
            activeSection === "employee" ? "rgba(99,102,241,0.06)" : "rgba(139,92,246,0.06)",
          border: `1px solid ${activeSection === "employee" ? "rgba(99,102,241,0.18)" : "rgba(139,92,246,0.18)"}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <AlertCircle
          size={15}
          strokeWidth={2}
          style={{
            color: activeSection === "employee" ? "#6366f1" : "#8b5cf6",
            flexShrink: 0,
            marginTop: 1,
          }}
        />
        <p
          style={{
            fontSize: "0.78rem",
            color: "var(--text-secondary)",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {supportLabel.HOW_IT_WORKS_DESC}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatCard
          label="Total"
          count={statsData.total}
          color="#6366f1"
          bg="rgba(99,102,241,0.06)"
          border="rgba(99,102,241,0.18)"
          icon={LifeBuoy}
        />
        <StatCard
          label="Open"
          count={statsData.open}
          color="#f59e0b"
          bg="rgba(245,158,11,0.06)"
          border="rgba(245,158,11,0.18)"
          icon={AlertCircle}
        />
        <StatCard
          label="In Progress"
          count={statsData.inProgress}
          color="#06b6d4"
          bg="rgba(6,182,212,0.06)"
          border="rgba(6,182,212,0.18)"
          icon={Clock}
        />
        <StatCard
          label="Resolved"
          count={statsData.resolved}
          color="#10b981"
          bg="rgba(16,185,129,0.06)"
          border="rgba(16,185,129,0.18)"
          icon={CheckCircle}
        />
        <StatCard
          label="Closed"
          count={statsData.closed}
          color="#64748b"
          bg="rgba(100,116,139,0.06)"
          border="rgba(100,116,139,0.18)"
          icon={XCircle}
        />
      </div>

      {/* Filters */}
      <div
        className="glass-sm"
        style={{
          padding: "12px 16px",
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
        }}
      >
        {/* Search */}
        <div
          style={{
            position: "relative",
            flex: width < 768 ? "1 1 100%" : "1 1 200px",
            minWidth: 160,
          }}
        >
          <Search
            size={14}
            strokeWidth={2}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            ref={(el) => {
              if (el) {
                el.style.setProperty("padding-left", "34px", "important");
                el.style.setProperty("padding-right", searchQuery ? "30px" : "12px", "important");
              }
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(100,116,139,0.1)",
                border: "none",
                padding: 3,
                cursor: "pointer",
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                borderRadius: 4,
                zIndex: 2,
              }}
            >
              <X size={12} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Status filter */}
        <div
          style={{
            flex: width < 768 ? "1 1 calc(50% - 8px)" : "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: width < 768 ? "100%" : "auto",
          }}
        >
          <Filter size={13} strokeWidth={2} style={{ color: "var(--text-muted)" }} />
          <select
            className="form-select"
            style={{ width: width < 768 ? "100%" : 130 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Priority filter */}
        <div
          style={{
            flex: width < 768 ? "1 1 calc(50% - 8px)" : "0 0 auto",
            width: width < 768 ? "100%" : "auto",
          }}
        >
          <select
            className="form-select"
            style={{ width: width < 768 ? "100%" : 130 }}
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="all">All Priority</option>
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main content: master-detail layout */}
      <div
        style={{
          display: "flex",
          flexDirection: width < 992 ? "column" : "row",
          gap: 16,
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        {/* Ticket list */}
        {(width >= 992 || !selectedTicket) && (
          <div
            className="glass"
            style={{
              flex: selectedTicket ? "0 0 55%" : "1 1 100%",
              width: "100%",
              overflow: "hidden",
              transition: "flex 0.3s ease",
            }}
          >
            {/* Table header */}
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid var(--glass-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h4
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  margin: 0,
                }}
              >
                {activeSection === "employee"
                  ? supportLabel.EMPLOYEE_SUPPORT
                  : supportLabel.PRODUCT_SUPPORT}
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: "0.72rem",
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "var(--icon-btn-bg)",
                    color: "var(--text-muted)",
                    border: "1px solid var(--glass-border)",
                    fontWeight: 600,
                  }}
                >
                  {filteredTickets.length}
                </span>
              </h4>
            </div>

            {/* Loading */}
            {isLoading && (
              <div style={{ padding: "0 20px" }}>
                {[...Array(4)].map((_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredTickets.length === 0 && (
              <div
                style={{
                  padding: "48px 20px",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: "var(--icon-btn-bg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LifeBuoy size={24} strokeWidth={1.5} style={{ color: "var(--text-muted)" }} />
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                      margin: 0,
                    }}
                  >
                    {supportLabel.NO_TICKETS}
                  </p>
                  <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "4px 0 0" }}>
                    {searchQuery || statusFilter !== "all" || priorityFilter !== "all"
                      ? "No tickets match your filters."
                      : supportLabel.CREATE_FIRST}
                  </p>
                </div>
                {!searchQuery && statusFilter === "all" && priorityFilter === "all" && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ marginTop: 4, fontSize: "0.8rem", padding: "8px 16px" }}
                    onClick={handleNewTicket}
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    {supportLabel.ADD_TICKET}
                  </button>
                )}
              </div>
            )}

            {/* Ticket table / card list */}
            {!isLoading && filteredTickets.length > 0 && (
              <>
                {useTableView ? (
                  /* Desktop table */
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "var(--table-header-bg)" }}>
                          {[
                            !showCompact && supportLabel.SR_NO,
                            supportLabel.TICKET_ID,
                            supportLabel.SUBJECT,
                            !showCompact && supportLabel.CATEGORY,
                            !showCompact && supportLabel.PRIORITY,
                            supportLabel.STATUS,
                            !showCompact && supportLabel.CREATED_AT,
                            supportLabel.ACTION,
                          ]
                            .filter(Boolean)
                            .map((col) => (
                              <th
                                key={col}
                                style={{
                                  padding: "10px 14px",
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  color: "var(--text-muted)",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  whiteSpace: "nowrap",
                                  borderBottom: "1px solid var(--table-border)",
                                }}
                              >
                                {col}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTickets.map((ticket, idx) => {
                          const isSelected = selectedTicket?.id === ticket?.id;
                          const categories =
                            ticket?.support_type === "product"
                              ? PRODUCT_CATEGORIES
                              : EMPLOYEE_CATEGORIES;
                          return (
                            <tr
                              key={ticket?.id ?? idx}
                              onClick={() => handleView(ticket)}
                              style={{
                                borderBottom: "1px solid var(--table-border)",
                                background: isSelected ? "rgba(99,102,241,0.07)" : "transparent",
                                cursor: "pointer",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected)
                                  e.currentTarget.style.background = "var(--table-row-hover)";
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) e.currentTarget.style.background = "transparent";
                              }}
                            >
                              {!showCompact && (
                                <td
                                  style={{
                                    padding: "11px 14px",
                                    fontSize: "0.78rem",
                                    color: "var(--text-muted)",
                                    fontWeight: 600,
                                  }}
                                >
                                  {idx + 1}
                                </td>
                              )}
                              <td
                                style={{
                                  padding: "11px 14px",
                                  fontSize: "0.78rem",
                                  color: "var(--brand-indigo)",
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                #{ticket?.[SUPPORT_ACCESSOR_KEYS.TICKET_NO] ?? "—"}
                              </td>
                              <td
                                style={{
                                  padding: "11px 14px",
                                  fontSize: "0.8125rem",
                                  color: "var(--text-primary)",
                                  fontWeight: 600,
                                  maxWidth: showCompact ? 120 : 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {ticket?.[SUPPORT_ACCESSOR_KEYS.SUBJECT] ?? "—"}
                              </td>
                              {!showCompact && (
                                <td
                                  style={{
                                    padding: "11px 14px",
                                    fontSize: "0.78rem",
                                    color: "var(--text-secondary)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {findLabel(categories, ticket?.[SUPPORT_ACCESSOR_KEYS.CATEGORY])}
                                </td>
                              )}
                              {!showCompact && (
                                <td style={{ padding: "11px 14px" }}>
                                  <PriorityBadge
                                    priority={ticket?.[SUPPORT_ACCESSOR_KEYS.PRIORITY]}
                                  />
                                </td>
                              )}
                              <td style={{ padding: "11px 14px" }}>
                                <StatusBadge status={ticket?.[SUPPORT_ACCESSOR_KEYS.STATUS]} />
                              </td>
                              {!showCompact && (
                                <td
                                  style={{
                                    padding: "11px 14px",
                                    fontSize: "0.75rem",
                                    color: "var(--text-muted)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {fmtDate(ticket?.[SUPPORT_ACCESSOR_KEYS.CREATED_AT])}
                                </td>
                              )}
                              <td style={{ padding: "11px 14px" }}>
                                <div
                                  style={{ display: "flex", gap: 5 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-icon-action btn-view"
                                    title={supportLabel.VIEW_TICKET}
                                    onClick={() => handleView(ticket)}
                                  >
                                    <Eye size={13} strokeWidth={2} />
                                  </button>
                                  {canManageSupport && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-icon-action btn-edit"
                                        title={supportLabel.EDIT_TICKET}
                                        onClick={() => handleEdit(ticket)}
                                        disabled={ticket?.status?.toLowerCase() === "closed"}
                                        style={{
                                          opacity:
                                            ticket?.status?.toLowerCase() === "closed" ? 0.5 : 1,
                                          cursor:
                                            ticket?.status?.toLowerCase() === "closed"
                                              ? "not-allowed"
                                              : "pointer",
                                        }}
                                      >
                                        <Edit2 size={13} strokeWidth={2} />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-icon-action btn-delete"
                                        title={supportLabel.DELETE_TICKET}
                                        onClick={() => handleDeleteClick(ticket)}
                                      >
                                        <Trash2 size={13} strokeWidth={2} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Mobile / compact cards */
                  <div
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {filteredTickets.map((ticket, idx) => (
                      <TicketCard
                        key={ticket?.id ?? idx}
                        ticket={ticket}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        isAdmin={canManageSupport}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Detail pane */}
        {selectedTicket && (
          <div
            className="animate-slide-up"
            style={{
              flex: width < 992 ? "1 1 100%" : "0 0 42%",
              width: "100%",
              minWidth: 0,
              position: width < 992 ? "static" : "sticky",
              top: 20,
            }}
          >
            <TicketDetail
              ticket={selectedTicket}
              onClose={() => setSelectedTicket(null)}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              isAdmin={canManageSupport}
              onStatusUpdate={handleStatusUpdate}
            />
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      <BaseModal
        isOpen={showFormModal}
        toggler={() => {
          setShowFormModal(false);
          setIsEdit(false);
          formik.resetForm();
        }}
        title={isEdit ? supportLabel.EDIT_TICKET : supportLabel.RAISE_TICKET_TITLE}
        submitText={isEdit ? "Update Ticket" : supportLabel.ADD_TICKET}
        loader={submitLoader}
        submit={formik.handleSubmit}
        size="md"
      >
        <TicketForm
          formik={formik}
          isEdit={isEdit}
          isViewOnly={isViewOnly}
          canEmployeeSupport={canEmployeeSupport}
          canProductSupport={canProductSupport}
        />
      </BaseModal>

      {/* Delete confirmation modal */}
      <BaseModal
        isOpen={showDeleteModal}
        toggler={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        title={supportLabel.DELETE_TICKET}
        submitText="Delete"
        loader={deleteLoader}
        submit={handleConfirmDelete}
        size="sm"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: "center",
            textAlign: "center",
            padding: "8px 0",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash2 size={22} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", margin: 0 }}>
            {supportLabel.DELETE_CONFIRM_MSG}
          </p>
          {deleteTarget && (
            <p
              style={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                margin: 0,
                padding: "8px 16px",
                borderRadius: 8,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              {deleteTarget?.subject}
            </p>
          )}
        </div>
      </BaseModal>
    </div>
  );
};

export default SupportCenter;
